import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext();

const AUTH_CONFIG = {
  apiUrl: process.env.REACT_APP_API_URL || 'https://api.coopdelta.com.br',
  authEndpoint: process.env.REACT_APP_AUTH_ENDPOINT || '/auth/login',

  usuarios: {
    'admin': 'a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6q7r8s9t0u1v2w3x4y5z6',
    'cooperativa': 'b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6q7r8s9t0u1v2w3x4y5z6a1',
    'gestao': 'c3d4e5f6g7h8i9j0k1l2m3n4o5p6q7r8s9t0u1v2w3x4y5z6a1b2'
  },
  
  sessionKey: 'coopdelta_auth_session',
  sessionTimeout: 8 * 60 * 60 * 1000 // 8 horas em milissegundos
};

const simpleHash = (text) => {
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    const char = text.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16);
};

const authenticateWithAPI = async (username, password) => {
  try {
    const response = await fetch(`${AUTH_CONFIG.apiUrl}${AUTH_CONFIG.authEndpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username,
        password,
        token: process.env.REACT_APP_API_TOKEN || '1234'
      })
    });

    if (response.ok) {
      const data = await response.json();
      return { success: true, user: data.user, token: data.token };
    } else {
      return { success: false, message: 'Credenciais inválidas' };
    }
  } catch (error) {
    console.error('Erro na autenticação:', error);
    return { success: false, message: 'Erro de conexão' };
  }
};

// Provedor do contexto de autenticação
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Verificar se há sessão ativa ao carregar
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Verificar status de autenticação
  const checkAuthStatus = () => {
    try {
      const sessionData = localStorage.getItem(AUTH_CONFIG.sessionKey);
      
      if (sessionData) {
        const session = JSON.parse(sessionData);
        const now = new Date().getTime();
        
        // Verificar se a sessão não expirou
        if (session.expiresAt && now < session.expiresAt) {
          setIsAuthenticated(true);
          setCurrentUser(session.user);
          
          // Estender a sessão automaticamente
          extendSession(session.user);
        } else {
          // Sessão expirada
          logout();
        }
      }
    } catch (error) {
      console.error('Erro ao verificar autenticação:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // Função de login com múltiplas opções de segurança
  const login = async (username, password) => {
    if (process.env.REACT_APP_USE_API_AUTH === 'true') {
      const result = await authenticateWithAPI(username, password);
      
      if (result.success) {
        const now = new Date().getTime();
        const expiresAt = now + AUTH_CONFIG.sessionTimeout;
        
        const sessionData = {
          user: username,
          loginTime: now,
          expiresAt: expiresAt,
          token: result.token
        };
        
        localStorage.setItem(AUTH_CONFIG.sessionKey, JSON.stringify(sessionData));
        setIsAuthenticated(true);
        setCurrentUser(username);
        
        return { success: true, message: 'Login realizado com sucesso!' };
      } else {
        return result;
      }
    }
    
    const passwordHash = simpleHash(password);
    const storedHash = AUTH_CONFIG.usuarios[username];
    
    const originalPasswords = {
      'admin': 'coopdelta2024',
      'cooperativa': 'uça123',
      'gestao': 'delta2024'
    };
    
    const isValidPassword = storedHash === passwordHash || 
                           originalPasswords[username] === password;
    
    if (username && isValidPassword) {
      const now = new Date().getTime();
      const expiresAt = now + AUTH_CONFIG.sessionTimeout;
      
      const sessionData = {
        user: username,
        loginTime: now,
        expiresAt: expiresAt
      };
      
      // Salvar sessão
      localStorage.setItem(AUTH_CONFIG.sessionKey, JSON.stringify(sessionData));
      
      setIsAuthenticated(true);
      setCurrentUser(username);
      
      return { success: true, message: 'Login realizado com sucesso!' };
    } else {
      return { success: false, message: 'Usuário ou senha incorretos.' };
    }
  };

  // Função de logout
  const logout = () => {
    localStorage.removeItem(AUTH_CONFIG.sessionKey);
    setIsAuthenticated(false);
    setCurrentUser(null);
  };

  // Estender sessão
  const extendSession = (username) => {
    const now = new Date().getTime();
    const expiresAt = now + AUTH_CONFIG.sessionTimeout;
    
    const sessionData = {
      user: username,
      loginTime: now,
      expiresAt: expiresAt
    };
    
    localStorage.setItem(AUTH_CONFIG.sessionKey, JSON.stringify(sessionData));
  };

  // Verificar se o usuário tem permissão para acessar uma rota
  const hasPermission = (route) => {
    if (!isAuthenticated) {
      // Rotas públicas
      const publicRoutes = ['/', '/fotos'];
      return publicRoutes.includes(route);
    }
    
    // Usuário autenticado tem acesso a tudo
    return true;
  };

  // Obter tempo restante da sessão
  const getTimeUntilExpiry = () => {
    try {
      const sessionData = localStorage.getItem(AUTH_CONFIG.sessionKey);
      if (sessionData) {
        const session = JSON.parse(sessionData);
        const now = new Date().getTime();
        const timeLeft = session.expiresAt - now;
        return Math.max(0, timeLeft);
      }
    } catch (error) {
      return 0;
    }
    return 0;
  };

  // Valores expostos pelo contexto
  const value = {
    isAuthenticated,
    currentUser,
    loading,
    login,
    logout,
    hasPermission,
    checkAuthStatus,
    getTimeUntilExpiry,
    extendSession: () => extendSession(currentUser)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook para facilitar o uso do contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  
  return context;
};

export default AuthContext;